/**
 * 🌌 Constellation Shop — /constellation route.
 *
 * The actual merchant stall. Only reachable while the user has an active
 * spawn. If they navigate here without one, we show a "He's not here right
 * now" empty state so the page never blanks out.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConstellationShop() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(null);  // item_id currently being bought

  const load = async () => {
    if (!isAuthed) return;
    try {
      const r = await axios.get(`${API}/constellation/state`);
      setState(r.data);
    } catch {
      toast.error("Failed to load Constellation Merchant.");
    }
  };

  useEffect(() => { load(); }, [isAuthed]);
  useEffect(() => {
    if (!state?.active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state?.active]);

  const buy = async (item_id) => {
    const it = state?.inventory?.find(x => x.item_id === item_id);
    const ok = await confirmDialog({
      title: `Buy ${it?.name || "item"}?`,
      message: `${it?.icon || ""} ${it?.name}
Cost: 💵 ${it?.price_od} OD. Deducts immediately.`,
      confirmLabel: `Buy — ${it?.price_od} OD`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(item_id);
    try {
      const r = await axios.post(`${API}/constellation/buy`, { item_id, qty: 1 });
      const it = state?.inventory?.find(x => x.item_id === item_id);
      toast.success(
        `${it?.icon || "🌌"} +1 ${it?.name || "item"} (bag: ${r.data.bag_count}) · −${r.data.spent_od} 💵 OD`,
        { duration: 4500 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBusy(null);
    }
  };

  if (!isAuthed) {
    return (
      <Frame>
        <div className="text-center py-20" data-testid="constellation-logged-out">
          <div className="text-6xl mb-3">🌌</div>
          <p className="text-neutral-400 mb-6">Sign in to find the Constellation Merchant.</p>
          <Link to="/auth" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("omni:open-auth")); }} className="inline-block px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold">
            Sign in
          </Link>
        </div>
      </Frame>
    );
  }
  if (!state) {
    return (
      <Frame>
        <div className="text-center py-24 text-amber-200 animate-pulse" data-testid="constellation-loading">
          🌌 Scanning the sky for him...
        </div>
      </Frame>
    );
  }

  if (!state.active) {
    return (
      <Frame>
        <EmptyState />
      </Frame>
    );
  }

  // Compute live countdown
  const exp = state.expires_at ? new Date(state.expires_at).getTime() : 0;
  const remaining = Math.max(0, Math.floor((exp - now) / 1000));
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <Frame>
      {/* Hero */}
      <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-[#1a0033] via-black/60 to-[#1f0f00] p-6 mb-5 relative overflow-hidden"
           data-testid="constellation-active-hero">
        <div className="absolute -top-6 -right-6 text-[140px] opacity-10 select-none">🌌</div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80 mb-1">
            One Hour Only · {state.merchant?.icon} {state.merchant?.name}
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-amber-200 mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}>
            {state.merchant?.tagline}
          </h1>
          <p className="text-sm text-amber-100/80 italic font-serif max-w-2xl">
            {state.merchant?.lore}
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
            <span data-testid="constellation-shop-countdown"
                  className="px-2 py-1 rounded bg-black/40 border border-amber-500/40 text-amber-200 font-black tabular-nums">
              ⏰ Closes in {mm}:{ss}
            </span>
            <span className="px-2 py-1 rounded bg-purple-900/40 border border-purple-600/40 text-purple-200">
              💵 {Number(state.omni_dollars || 0).toLocaleString()} OD on hand
            </span>
            {state.visits_count > 1 && (
              <span className="px-2 py-1 rounded bg-amber-900/30 border border-amber-700/40 text-amber-200">
                Visit #{state.visits_count}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Inventory grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="constellation-inventory-grid">
        {(state.inventory || []).map((it) => {
          const isAstral = it.item_id === "astral_coin";
          const soldOut = (it.stock_remaining || 0) <= 0 && it.enabled;
          const insufficientOd = (state.omni_dollars || 0) < it.price_od;
          const canBuy = it.enabled && !soldOut && !insufficientOd && !busy;
          return (
            <div key={it.item_id}
                 data-testid={`constellation-item-${it.item_id}`}
                 className={`rounded-xl border p-4 transition ${
                   it.enabled
                     ? "border-amber-500/40 bg-black/40 hover:border-amber-400/70"
                     : "border-neutral-700 bg-black/20 opacity-60"
                 }`}>
              <div className="flex items-start gap-3">
                <div className="text-5xl flex-shrink-0">{it.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-black text-lg text-amber-100"
                         style={{ fontFamily: "Anton, sans-serif" }}>
                      {it.name}
                    </div>
                    {isAstral && (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider bg-blue-900/40 border border-blue-700/50 text-blue-200">
                        🔒 Merchant-exclusive
                      </span>
                    )}
                    {it.rarity === "exclusive" && (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider bg-purple-900/40 border border-purple-600/50 text-purple-200">
                        ★ exclusive
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-amber-300/80 italic mt-1 font-serif">
                    {it.tagline}
                  </div>
                  <div className="text-xs text-neutral-300 mt-2 leading-snug">
                    {it.description}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    <span className="font-black text-amber-300 text-sm">
                      💵 {it.price_od} OD
                    </span>
                    {it.enabled && (
                      <span data-testid={`constellation-stock-${it.item_id}`}
                            className={`text-[10px] uppercase tracking-wider ${
                              soldOut ? "text-red-400" : "text-neutral-400"
                            }`}>
                        Stock: {it.stock_remaining} / {it.stock_per_spawn}
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    {!it.enabled ? (
                      <button
                        disabled
                        data-testid={`constellation-buy-disabled-${it.item_id}`}
                        title={it.disabled_reason}
                        className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-neutral-800 border border-neutral-700 text-neutral-500 cursor-not-allowed"
                      >
                        🚧 Coming Soon
                      </button>
                    ) : soldOut ? (
                      <button
                        disabled
                        data-testid={`constellation-soldout-${it.item_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-red-900/40 border border-red-700/40 text-red-300 cursor-not-allowed"
                      >
                        Sold Out
                      </button>
                    ) : insufficientOd ? (
                      <button
                        disabled
                        data-testid={`constellation-poor-${it.item_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-yellow-900/30 border border-yellow-700/40 text-yellow-300 cursor-not-allowed"
                      >
                        Need {it.price_od - (state.omni_dollars || 0)} more 💵
                      </button>
                    ) : (
                      <button
                        disabled={!canBuy}
                        onClick={() => buy(it.item_id)}
                        data-testid={`constellation-buy-${it.item_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black disabled:bg-neutral-800 disabled:text-neutral-500 transition"
                      >
                        {busy === it.item_id ? "Buying..." : `🌟 Buy for ${it.price_od} 💵`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}


function Frame({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0033] via-black to-[#1a0010] text-amber-100"
         data-testid="constellation-shop-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6 pb-24">
        {children}
      </main>
      <Footer />
    </div>
  );
}


function EmptyState() {
  return (
    <div className="text-center py-20" data-testid="constellation-no-active">
      <div className="text-7xl mb-3 opacity-50">🌌</div>
      <h1 className="text-3xl font-black text-amber-200 mb-2"
          style={{ fontFamily: "Anton, sans-serif" }}>
        He's Not Here Right Now
      </h1>
      <p className="text-neutral-400 max-w-xl mx-auto mb-3">
        The Constellation Merchant appears at random — never on a schedule.
        Each visit lasts <b>exactly one hour</b>. Keep your eyes open: a
        sticky banner will light up your screen the moment he arrives.
      </p>
      <p className="text-[10px] text-neutral-500 uppercase tracking-widest">
        Per-user spawn · 20% chance per check · 24h cooldown · 1 hour to shop
      </p>
      <Link to="/" className="inline-block mt-6 px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold">
        ← Back to OmniMuscle
      </Link>
    </div>
  );
}
